import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import FileDropzone from '../components/shared/FileDropzone';
import AttachmentList from '../components/shared/AttachmentList';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, X, Play, Send, MessageSquare, ArrowRight, User,
  Package, MapPin, Building2, Calendar, AlertCircle, CheckCircle2, Ticket, RefreshCw,
  Paperclip, Trash2,
} from 'lucide-react';

const PRIORITY_CONFIG = {
  low:    { label: 'Scazut',  class: 'bg-emerald-100 text-emerald-800' },
  medium: { label: 'Mediu',   class: 'bg-blue-100 text-blue-800' },
  high:   { label: 'Ridicat', class: 'bg-amber-100 text-amber-800' },
  urgent: { label: 'URGENT',  class: 'bg-red-100 text-red-800 animate-pulse' },
};
const STATUS_CONFIG = {
  new:         { label: 'Nou',        class: 'bg-blue-100 text-blue-800' },
  accepted:    { label: 'Acceptat',   class: 'bg-indigo-100 text-indigo-800' },
  in_progress: { label: 'In lucru',   class: 'bg-amber-100 text-amber-800' },
  completed:   { label: 'Finalizat',  class: 'bg-emerald-100 text-emerald-800' },
  rejected:    { label: 'Respins',    class: 'bg-rose-100 text-rose-800' },
};
const CATEGORY_LABELS = {
  cleaning: 'Curatenie', equipment_prep: 'Pregatire utilaj', visual_check: 'Verificare vizuala',
  logistics: 'Logistica', shift_handover: 'Comunicare tura', other: 'Altele',
};

const CrossDeptTaskDetailPage = ({ portal = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingTaskFiles, setPendingTaskFiles] = useState([]);
  const [pendingCommentFiles, setPendingCommentFiles] = useState([]);

  const fetchTask = useCallback(async () => {
    try {
      const res = await api.get(`/cross-dept-tasks/${id}`);
      setTask(res.data);
    } catch (e) {
      toast.error('Eroare la incarcare task');
      navigate(portal ? '/portal/cross-dept-tasks' : '/cross-dept-tasks');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, portal]);

  useEffect(() => { fetchTask(); }, [fetchTask]);

  const changeStatus = async (newStatus, reason = null) => {
    try {
      setSubmitting(true);
      const payload = { status: newStatus };
      if (reason) payload.reject_reason = reason;
      const res = await api.post(`/cross-dept-tasks/${id}/status`, payload);
      setTask(res.data);
      toast.success('Status actualizat');
      setShowRejectDialog(false);
      setRejectReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare');
    } finally { setSubmitting(false); }
  };

  const addComment = async () => {
    const hasText = !!comment.trim();
    const hasFiles = pendingCommentFiles.length > 0;
    if (!hasText && !hasFiles) return;
    try {
      setSubmitting(true);
      if (hasFiles) {
        // Multipart: send text + files in a single request to /with-attachments
        const fd = new FormData();
        fd.append('text', comment.trim());
        pendingCommentFiles.forEach((f) => fd.append('files', f));
        const res = await api.post(`/cross-dept-tasks/${id}/comments/with-attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setTask(res.data);
      } else {
        const res = await api.post(`/cross-dept-tasks/${id}/comments`, { text: comment.trim() });
        setTask(res.data);
      }
      setComment('');
      setPendingCommentFiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la trimitere comentariu');
    } finally { setSubmitting(false); }
  };

  const promoteToTicket = async () => {
    if (!window.confirm('Promovezi acest task intr-un tichet oficial de mentenanta?')) return;
    try {
      setSubmitting(true);
      const res = await api.post(`/cross-dept-tasks/${id}/promote-to-ticket`);
      toast.success('Task promovat in tichet');
      fetchTask();
      // Option: navigate to ticket detail
      if (res.data.ticket_id && !portal) {
        setTimeout(() => navigate(`/tickets/${res.data.ticket_id}`), 800);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la promovare');
    } finally { setSubmitting(false); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const uploadTaskFiles = async () => {
    if (pendingTaskFiles.length === 0) return;
    setUploading(true);
    try {
      for (const file of pendingTaskFiles) {
        if (file.size > 50 * 1024 * 1024) {
          toast.error(`${file.name}: depaseste 50MB`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/cross-dept-tasks/${id}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await fetchTask();
      setPendingTaskFiles([]);
      toast.success('Fisiere incarcate');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la incarcare');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm(`Sigur stergi atasamentul "${att.name}"?`)) return;
    try {
      await api.delete(`/cross-dept-tasks/${id}/attachments/${att.id}`);
      await fetchTask();
      toast.success('Atasament sters');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la stergere');
    }
  };

  const handleDeleteTask = async () => {
    try {
      setSubmitting(true);
      await api.delete(`/cross-dept-tasks/${id}`);
      toast.success('Task mutat in Cosul de gunoi');
      navigate(portal ? '/portal/cross-dept-tasks' : '/cross-dept-tasks');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la stergere');
    } finally {
      setSubmitting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading || !task) {
    return <div className={portal ? 'max-w-5xl mx-auto p-6' : 'p-6'}><RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto" /></div>;
  }

  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const status = STATUS_CONFIG[task.status] || STATUS_CONFIG.new;
  const categoryLabel = task.category === 'other' && task.custom_category ? task.custom_category : (CATEGORY_LABELS[task.category] || task.category);
  const isCreator = task.from_user_id === user?.id;
  const isAdmin = user?.role === 'admin';
  const canAdvance = ['admin', 'manager', 'technician', 'section_chief'].includes(user?.role);
  const canPromote = task.equipment_id && task.plant_id && task.location_id && !task.promoted_to_ticket_id &&
                     ['admin', 'manager', 'technician'].includes(user?.role);

  const backPath = portal ? '/portal/cross-dept-tasks' : '/cross-dept-tasks';

  return (
    <div className={portal ? 'max-w-5xl mx-auto px-3 md:px-4 py-3 md:py-6 pb-24' : 'space-y-4 md:space-y-6 max-w-5xl pb-24 md:pb-0'} data-testid="cross-dept-task-detail-page">
      <Button variant="ghost" onClick={() => navigate(backPath)} className="mb-2 md:mb-3 -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Inapoi la task-uri
      </Button>

      {/* Header card */}
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mb-2">
                <Badge className={`${status.class} text-[10px] md:text-xs`}>{status.label}</Badge>
                <Badge className={`${priority.class} text-[10px] md:text-xs`}>{priority.label}</Badge>
                <Badge variant="outline" className="text-[10px] md:text-xs">{categoryLabel}</Badge>
                {task.promoted_to_ticket_id && (
                  <Badge className="bg-purple-100 text-purple-700 text-[10px] md:text-xs">
                    <Ticket className="w-3 h-3 mr-1" /> Tichet
                  </Badge>
                )}
              </div>
              <h1 className="text-lg md:text-3xl font-bold text-slate-900 break-words">{task.title}</h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 md:mt-3 text-xs md:text-sm text-slate-600">
                <span className="flex items-center gap-1"><User className="w-3.5 h-3.5 md:w-4 md:h-4" /> <b>{task.from_user_name}</b> <span className="hidden md:inline">({task.from_department})</span></span>
                <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 text-slate-400" />
                <span className="flex items-center gap-1"><b>{task.to_user_name || task.to_department}</b></span>
              </div>
            </div>
          </div>

          <div className="mt-4 md:mt-5 whitespace-pre-wrap text-sm md:text-base text-slate-800 bg-slate-50 p-3 md:p-4 rounded-lg border border-slate-200">
            {task.description}
          </div>

          {/* Metadata */}
          <div className="mt-4 md:mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 text-xs md:text-sm">
            {task.equipment_name && <Info icon={Package} label="Echipament" value={`${task.equipment_name} (${task.equipment_code || '-'})`} />}
            {task.plant_name && <Info icon={Building2} label="Sediu" value={task.plant_name} />}
            {task.location_name && <Info icon={MapPin} label="Locatie" value={task.location_name} />}
            {task.due_date && <Info icon={Calendar} label="Termen" value={task.due_date} />}
            <Info icon={Calendar} label="Creat" value={formatDate(task.created_at)} />
            {task.accepted_at && <Info icon={Check} label="Acceptat" value={formatDate(task.accepted_at)} />}
            {task.completed_at && <Info icon={CheckCircle2} label="Finalizat" value={formatDate(task.completed_at)} />}
          </div>

          {/* Reject reason */}
          {task.status === 'rejected' && task.reject_reason && (
            <div className="mt-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <div><b>Motiv respingere:</b> {task.reject_reason}</div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-5 md:mt-6 grid grid-cols-2 md:flex md:flex-wrap gap-2">
            {canAdvance && task.status === 'new' && (
              <>
                <Button onClick={() => changeStatus('accepted')} disabled={submitting} className="h-11 md:h-10 bg-indigo-600 hover:bg-indigo-700" data-testid="accept-task-btn">
                  <Check className="w-4 h-4 mr-2" /> Accept
                </Button>
                <Button onClick={() => changeStatus('in_progress')} disabled={submitting} variant="outline" className="h-11 md:h-10" data-testid="start-task-btn">
                  <Play className="w-4 h-4 mr-2" /> Incep
                </Button>
                <Button onClick={() => setShowRejectDialog(true)} disabled={submitting} variant="outline" className="h-11 md:h-10 text-rose-600 hover:bg-rose-50 col-span-2 md:col-span-1" data-testid="reject-task-btn">
                  <X className="w-4 h-4 mr-2" /> Respinge
                </Button>
              </>
            )}
            {canAdvance && task.status === 'accepted' && (
              <>
                <Button onClick={() => changeStatus('in_progress')} disabled={submitting} className="h-11 md:h-10 bg-amber-600 hover:bg-amber-700" data-testid="start-task-btn-2">
                  <Play className="w-4 h-4 mr-2" /> Incep lucrul
                </Button>
                <Button onClick={() => changeStatus('completed')} disabled={submitting} variant="outline" className="h-11 md:h-10 text-emerald-700 border-emerald-300" data-testid="complete-task-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Finalizeaza
                </Button>
              </>
            )}
            {canAdvance && task.status === 'in_progress' && (
              <Button onClick={() => changeStatus('completed')} disabled={submitting} className="h-11 md:h-10 col-span-2 md:col-auto bg-emerald-600 hover:bg-emerald-700" data-testid="complete-task-btn-2">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Marcheaza ca finalizat
              </Button>
            )}
            {isAdmin && task.status === 'rejected' && (
              <Button onClick={() => changeStatus('new')} disabled={submitting} variant="outline" className="h-11 md:h-10 col-span-2 md:col-auto">
                Redeschide task
              </Button>
            )}
            {canPromote && (
              <Button onClick={promoteToTicket} disabled={submitting} className="h-11 md:h-10 col-span-2 md:col-auto bg-purple-600 hover:bg-purple-700 md:ml-auto" data-testid="promote-to-ticket-btn">
                <Ticket className="w-4 h-4 mr-2" /> Promoveaza in tichet
              </Button>
            )}
            {task.promoted_to_ticket_id && !portal && (
              <Button onClick={() => navigate(`/tickets/${task.promoted_to_ticket_id}`)} variant="outline" className="h-11 md:h-10 col-span-2 md:col-auto md:ml-auto">
                <ArrowRight className="w-4 h-4 mr-2" /> Deschide tichetul creat
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => setShowDeleteDialog(true)}
                disabled={submitting}
                variant="outline"
                className="h-11 md:h-10 col-span-2 md:col-auto text-rose-600 border-rose-200 hover:bg-rose-50"
                data-testid="delete-task-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Sterge (Cos de gunoi)
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Attachments */}
      <Card className="mt-4 md:mt-6">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center gap-2 mb-3 md:mb-4">
            <Paperclip className="w-5 h-5 text-indigo-600" />
            <h2 className="text-base md:text-lg font-semibold">
              Atasamente ({(task.attachments || []).length})
            </h2>
          </div>

          {task.status !== 'completed' && (
            <div className="mb-4">
              <FileDropzone
                files={pendingTaskFiles}
                onFilesChange={setPendingTaskFiles}
                testIdPrefix="task-attach"
              />
              {pendingTaskFiles.length > 0 && (
                <div className="mt-3 flex justify-end">
                  <Button
                    onClick={uploadTaskFiles}
                    disabled={uploading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="task-attachment-upload-btn"
                  >
                    {uploading ? 'Se incarca...' : `Incarca ${pendingTaskFiles.length} fisier(e)`}
                  </Button>
                </div>
              )}
            </div>
          )}

          {(task.attachments || []).length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              Niciun atasament incarcat inca.
            </p>
          ) : (
            <AttachmentList
              attachments={task.attachments || []}
              canDeleteFn={(a) => task.status !== 'completed' && (isAdmin || a.uploaded_by_id === user?.id)}
              onDelete={handleDeleteAttachment}
              testIdPrefix="task-attachment"
            />
          )}
        </CardContent>
      </Card>

      {/* Comments thread */}
      <Card className="mt-4 md:mt-6">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center gap-2 mb-3 md:mb-4">
            <MessageSquare className="w-5 h-5 text-indigo-600" />
            <h2 className="text-base md:text-lg font-semibold">Conversatie ({(task.comments || []).length})</h2>
          </div>

          <div className="space-y-3 max-h-80 md:max-h-96 overflow-y-auto">
            {(task.comments || []).length === 0 && (
              <p className="text-sm text-slate-500 italic">Niciun mesaj inca. Scrie primul mesaj pentru a porni conversatia.</p>
            )}
            {(task.comments || []).map(c => {
              const isSystem = c.system;
              const isMe = c.user_id === user?.id;
              return (
                <div key={c.id} className={`flex ${isSystem ? 'justify-center' : isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] md:max-w-[80%] rounded-lg p-2.5 md:p-3 text-sm ${
                    isSystem
                      ? 'bg-slate-100 text-slate-600 italic text-xs border border-slate-200'
                      : isMe
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-100 text-slate-800'
                  }`}>
                    {!isSystem && (
                      <div className={`text-[11px] font-semibold mb-0.5 ${isMe ? 'text-indigo-100' : 'text-slate-500'}`}>
                        {c.user_name} · {formatDate(c.created_at)}
                      </div>
                    )}
                    {c.text && <div className="whitespace-pre-wrap">{c.text}</div>}
                    {(c.attachments && c.attachments.length > 0) && (
                      <div className={`${c.text ? 'mt-2' : ''} ${isMe ? 'bg-indigo-500/30 rounded p-1' : ''}`}>
                        <AttachmentList
                          attachments={c.attachments}
                          compact
                          testIdPrefix={`comment-${c.id}-attach`}
                        />
                      </div>
                    )}
                    {isSystem && <div className="text-[10px] opacity-60 mt-0.5">{formatDate(c.created_at)}</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {task.status !== 'completed' && (
            <div className="mt-4 space-y-2">
              {pendingCommentFiles.length > 0 && (
                <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-200">
                  <div className="text-xs font-semibold text-indigo-700 mb-1.5">
                    {pendingCommentFiles.length} fisier(e) atasate la mesaj:
                  </div>
                  <FileDropzone
                    files={pendingCommentFiles}
                    onFilesChange={setPendingCommentFiles}
                    compact
                    testIdPrefix="comment-attach"
                  />
                </div>
              )}
              <div className="flex gap-2 items-end">
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  placeholder="Scrie un mesaj..."
                  className="flex-1 min-h-[44px]"
                  data-testid="task-comment-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                />
                <label
                  htmlFor="comment-file-input-native"
                  className="h-11 w-11 md:h-10 md:w-10 flex items-center justify-center rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-slate-600 flex-shrink-0"
                  title="Ataseaza fisiere la mesaj"
                  data-testid="comment-attach-btn"
                >
                  <Paperclip className="w-4 h-4" />
                </label>
                <input
                  id="comment-file-input-native"
                  type="file"
                  multiple
                  hidden
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                  onChange={(e) => {
                    const arr = Array.from(e.target.files || []);
                    const valid = arr.filter((f) => {
                      if (f.size > 50 * 1024 * 1024) {
                        toast.error(`${f.name}: depaseste 50MB`);
                        return false;
                      }
                      return true;
                    });
                    setPendingCommentFiles((prev) => [...prev, ...valid]);
                    e.target.value = '';
                  }}
                />
                <Button
                  onClick={addComment}
                  disabled={submitting || (!comment.trim() && pendingCommentFiles.length === 0)}
                  className="h-11 w-11 md:h-10 md:w-auto p-0 md:px-4 bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
                  data-testid="task-comment-submit"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Task Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-task-dialog">
          <DialogHeader><DialogTitle>Muta task-ul in Cosul de gunoi?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Task-ul <b>"{task.title}"</b> va fi mutat in Cosul de gunoi. Poate fi restaurat ulterior de un administrator din <b>Setari &gt; Cosul de gunoi</b>.
            </p>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>Stergerea este reversibila. Doar administratorul poate efectua aceasta actiune.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={submitting}>Anuleaza</Button>
            <Button
              onClick={handleDeleteTask}
              disabled={submitting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-task-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Muta in Cos de gunoi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent data-testid="reject-task-dialog">
          <DialogHeader><DialogTitle>Motivul respingerii</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600">Introdu motivul pentru care respingi acest task. Va fi vizibil pentru solicitant.</p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Ex: echipamentul nu este disponibil acum, sau cererea trebuie reformulata..."
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Anuleaza</Button>
            <Button
              onClick={() => rejectReason.trim() ? changeStatus('rejected', rejectReason.trim()) : toast.error('Motivul este obligatoriu')}
              disabled={submitting || !rejectReason.trim()}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-reject-btn"
            >
              <X className="w-4 h-4 mr-2" /> Respinge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="font-medium text-slate-800">{value}</p>
    </div>
  </div>
);

export default CrossDeptTaskDetailPage;
